import { AuthUser, IAuthUser } from '@order-up/models';
import { AuthUserSchema } from '@order-up/schemas';
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  deleteUser as firebaseDeleteUser,
  linkWithCredential,
  PhoneAuthProvider,
  reauthenticateWithCredential,
  signInAnonymously as firebaseSignInAnonymously,
  signInWithCredential,
  signInWithPhoneNumber as firebaseSignInWithPhoneNumber,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  IdTokenResult,
  OAuthProvider,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
  UserCredential,
  verifyBeforeUpdateEmail as firebaseVerifyBeforeUpdateEmail,
  type ApplicationVerifier,
  type ConfirmationResult,
  type MultiFactorResolver,
  type User as FirebaseUser,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { getFirebaseAuth } from '../firebase/config';
import { resolveTotpSignIn } from './mfa';
import { createLogger } from '../utils/logger';
import { broadcastAuthChange } from './crossAppAuth';
import type { AxiosRequestConfig } from 'axios';
import { createApiClient, type NoRecentAuthPromptRequest } from './apiClient';

const logger = createLogger('AuthService');

/** Shared client for direct API calls from auth service.
 *  Uses the centralized factory (Bearer + App Check + session refresh). */
const authApiClient = createApiClient('/api/v1/auth');
const usersApiClient = createApiClient('/api/v1/users');

export const mapFirebaseUserToAuthUser = (firebaseUser: FirebaseUser): AuthUser => {
  const authUserData: IAuthUser = {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    emailVerified: firebaseUser.emailVerified,
    isAnonymous: firebaseUser.isAnonymous,
  };
  const validatedData = AuthUserSchema.parse(authUserData);
  return new AuthUser(validatedData);
};

const signIn = async (email: string, password: string): Promise<AuthUser> => {
  logger.debug('Attempting to sign in user', { email });
  const auth = getFirebaseAuth();
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const _token = await getCurrentUserToken(true); // Force fresh token after signin
  logger.debug('User signed in successfully', { uid: userCredential.user.uid });
  return mapFirebaseUserToAuthUser(userCredential.user);
}

/**
 * Complete a sign-in that Firebase interrupted with the MFA challenge
 * (`auth/multi-factor-auth-required`). The caller extracts the resolver from
 * the thrown error via `getMfaResolver(error)`, collects a TOTP code, then
 * calls this. On success the `onAuthStateChanged` listener updates the cache
 * exactly as a normal sign-in does; we force a fresh token first so the new
 * `sign_in_second_factor` claim is present on the very next request.
 */
const resolveMfaSignIn = async (
  resolver: MultiFactorResolver,
  code: string,
): Promise<AuthUser> => {
  const cred = await resolveTotpSignIn(resolver, code);
  await getCurrentUserToken(true);
  logger.debug('MFA sign-in resolved', { uid: cred.user.uid });
  return mapFirebaseUserToAuthUser(cred.user);
};

const signUp = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  options: { skipVerificationEmail?: boolean } = {},
): Promise<FirebaseUser> => {
  logger.debug('Attempting to sign up new user', { email, firstName, lastName });
  const auth = getFirebaseAuth();
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  const displayName = `${firstName} ${lastName}`;

  // Update the Firebase Auth user's profile immediately after creation.
  await updateProfile(user, { displayName });

  // Force reload the user to get the updated profile
  await user.reload();

  const token = await getCurrentUserToken(true); // Force fresh token after signup
  logger.debug('User signed up successfully', { uid: user.uid, displayName });

  // Send verification email via our API (uses custom branded email templates).
  // Target is the newly-created user's UID (self-service — they're now signed in).
  //
  // Skipped when the account is being created to accept an invitation: the
  // invitation link already proved the address, and `acceptInvitation` sets
  // `emailVerified: true` on the server a moment later. Sending one anyway
  // mailed a new hire a "verify your email" for an address that was verified
  // before they could open it — in English, under a Spanish invitation.
  if (token && !options.skipVerificationEmail) {
    try {
      await authApiClient.post('/send-verification', { userId: user.uid });
      logger.debug('Custom verification email sent via API', { email, uid: user.uid });
    } catch (emailError) {
      // Don't fail signup if verification email fails - user can resend
      logger.warn('Failed to send verification email via API, user can resend later', {
        error: emailError instanceof Error ? emailError.message : String(emailError),
      });
    }
  }

  // Return the raw Firebase user object. The UI layer will handle the next steps.
  return user;
}

const signInAnonymously = async (): Promise<AuthUser> => {
  logger.debug('Signing in anonymously for guest access');
  const auth = getFirebaseAuth();
  const userCredential = await firebaseSignInAnonymously(auth);
  logger.debug('Anonymous sign-in successful', { uid: userCredential.user.uid });

  // IMPORTANT: Also establish server session for anonymous users
  // This creates a session cookie that allows API calls (orders, payments, etc.)
  // We defer this to avoid circular imports - caller should use adminApiService.signInWithIdToken
  // after anonymous sign-in completes (see useAutoAnonymousAuth hook)

  return mapFirebaseUserToAuthUser(userCredential.user);
};

const signOut = (): Promise<void> => {
  logger.debug('Signing out current user');
  const auth = getFirebaseAuth();
  return firebaseSignOut(auth);
}

const crossAppSignOut = async (): Promise<void> => {
  logger.debug('Cross-app signout initiated');
  const auth = getFirebaseAuth();

  // Broadcast before signing out to ensure other apps get the event
  broadcastAuthChange('signout');

  // Sign out locally
  await firebaseSignOut(auth);

  logger.info('Cross-app signout completed and broadcasted');
}

/**
 * Requests a password-reset link for `email`.
 *
 * Routed through the API — NEVER the Firebase client SDK — so the link is
 * delivered by OrderUp's own branded, DKIM-signed pipeline, where a send is
 * logged and its bounces are visible. The client-SDK call handed delivery to
 * Firebase's mailer, which reports nothing back: its promise resolving meant
 * only that Firebase had accepted the request, so a customer who never
 * received the mail and a customer who did looked identical from here.
 *
 * Resolving stays enumeration-safe — an unknown address resolves exactly as a
 * known one does, so this can never be used to probe which emails are
 * registered. It REJECTS only when OUR OWN delivery failed, which reveals
 * nothing about the account and must not be shown to the user as success.
 */
const sendPasswordResetEmail = async (email: string, locale?: string): Promise<void> => {
  // The address itself is deliberately not logged — this is an unauthenticated
  // endpoint, so the argument is attacker-controlled and needs no client trace.
  logger.debug('Requesting password reset email via API');
  // `locale`: the form's language; the email and Firebase's reset page follow it.
  await authApiClient.post('/forgot-password', locale ? { email, locale } : { email });
}

export interface SendVerificationEmailResult {
  /** True when the target was already verified and no email was actually sent. */
  alreadyVerified: boolean;
  /** Server-provided status message. */
  message: string;
}

/**
 * Sends a verification email to the target user.
 *
 * The target is always explicit — pass the UID of the user who should receive
 * the verification link. The authenticated caller must either BE that user
 * (self-service) or have admin privileges (admin-initiated). Authorization is
 * enforced server-side.
 *
 * Returns a result object so callers can distinguish "email dispatched" from
 * "target was already verified" — the server short-circuits verified users
 * with a 200 to keep the call idempotent.
 */
const sendVerificationEmail = async (
  userId: string
): Promise<SendVerificationEmailResult> => {
  const token = await getCurrentUserToken();
  if (!token) {
    logger.error('Cannot send verification email - no user is currently signed in');
    throw new Error("Cannot send verification email. No user is currently signed in.");
  }

  logger.debug('Sending verification email via API', { targetUid: userId });

  // apiClient interceptor unwraps to response.data, so this is the Fastify envelope.
  const envelope = (await authApiClient.post('/send-verification', { userId })) as unknown as {
    success: boolean;
    data?: { message?: string; alreadyVerified?: boolean };
  };

  const result: SendVerificationEmailResult = {
    alreadyVerified: Boolean(envelope?.data?.alreadyVerified),
    message: envelope?.data?.message ?? 'Verification email sent.',
  };

  logger.debug('Verification email request accepted', {
    alreadyVerified: result.alreadyVerified,
  });

  return result;
}

/**
 * Deletes the currently authenticated user from Firebase Auth.
 * This can only be used for the current user due to Firebase Auth limitations.
 */
const deleteCurrentUser = async (): Promise<void> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    logger.error('Cannot delete user - no user is currently signed in');
    throw new Error("Cannot delete user. No user is currently signed in.");
  }
  logger.debug('Deleting current user', { uid: user.uid });
  await firebaseDeleteUser(user);
  logger.debug('User deleted successfully', { uid: user.uid });
}

/**
 * Updates the current user's Firebase Auth profile.
 * @param updates Object containing displayName and/or photoURL to update
 */
const updateUserProfile = async (updates: { displayName?: string; photoURL?: string }): Promise<void> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    logger.error('Cannot update profile - no user is currently signed in');
    throw new Error("Cannot update profile. No user is currently signed in.");
  }
  logger.debug('Updating profile for user', { uid: user.uid, updates });
  await updateProfile(user, updates);
  logger.debug('Profile updated successfully for user', { uid: user.uid });
}

/**
 * Re-authenticate the current user with their email + CURRENT password.
 * Proves knowledge of the existing password before a sensitive change
 * (password / email update). Throws the raw Firebase auth error
 * (e.g. `auth/wrong-password`, `auth/too-many-requests`) so the caller can
 * map it to user-facing copy.
 */
const reauthenticateWithPassword = async (currentPassword: string): Promise<void> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('Cannot re-authenticate. No password-based user is signed in.');
  }
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  logger.debug('Re-authentication successful', { uid: user.uid });
};

/**
 * Update the current user's password. The caller MUST re-authenticate first
 * (see {@link reauthenticateWithPassword}) — Firebase requires a recent login
 * for this operation and throws `auth/requires-recent-login` otherwise.
 */
const updatePassword = async (newPassword: string): Promise<void> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Cannot update password. No user is currently signed in.');
  }
  await firebaseUpdatePassword(user, newPassword);
  logger.debug('Password updated successfully', { uid: user.uid });
};

/**
 * Start a verify-before-update email change. Firebase sends a verification
 * link to the NEW address; the login email changes ONLY once the user clicks
 * it — so a typo can never lock the user out (unlike an Admin-SDK
 * `updateUser({ email })`, which switches the login email immediately).
 * Requires a recent login (`auth/requires-recent-login` otherwise).
 */
const verifyBeforeUpdateEmail = async (newEmail: string): Promise<void> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Cannot update email. No user is currently signed in.');
  }
  await firebaseVerifyBeforeUpdateEmail(user, newEmail);
  logger.debug('Verification email sent for email change', { uid: user.uid });
};

// ── Account changes through the API (SOC 2 R74) ─────────────────────────────
//
// A password or sign-in email changed with the client SDK never reaches the
// API, so nobody is told and nothing is audited. While the API's
// `AUTH_CHANGES_VIA_API` switch is on, the hooks in `useAuth.ts` use these
// instead: the SERVER makes the change and sends the notice, so a client that
// skips a step cannot skip the notice.

/**
 * Whether this deployment routes account changes through the API. Asked before
 * each change, so turning the switch on or off needs no client rebuild. Any
 * failure — an API that predates the route (404), a network error — answers
 * false: the Firebase path is the one that worked before R74.
 */
const isAccountChangeRoutedThroughApi = async (): Promise<boolean> => {
  try {
    const envelope = (await authApiClient.get('/account-change-routing')) as unknown as {
      data?: { viaApi?: unknown };
    };
    return envelope?.data?.viaApi === true;
  } catch (error) {
    logger.warn('Account change routing unreadable; using the Firebase path', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Change the password on the server (`PUT /auth/change-password`): it checks
 * a recent sign-in AND the current password, signs out every session, and
 * tells the account. Call {@link reauthenticateWithPassword} first so the
 * session the server reads is fresh.
 */
const changePasswordThroughApi = async (currentPassword: string, newPassword: string): Promise<void> => {
  await authApiClient.put('/change-password', { currentPassword, newPassword, confirmPassword: newPassword });
};

/**
 * Ask the server to move the sign-in email (`POST
 * /users/me/email/verify-and-change`): it emails the confirmation link to the
 * new address and a notice to the current one. Nothing changes until the link
 * is opened.
 */
const requestEmailChangeThroughApi = async (newEmail: string): Promise<void> => {
  await usersApiClient.post('/me/email/verify-and-change', { newEmail });
};

/**
 * Complete a password reset on the server (`POST /auth/reset-password`) with
 * the code from the reset email. Used by the auth app's action page; no
 * session is needed or sent.
 */
const confirmPasswordResetThroughApi = async (oobCode: string, newPassword: string): Promise<void> => {
  await authApiClient.post('/reset-password', { token: oobCode, newPassword, confirmPassword: newPassword });
};

/**
 * Restore Firebase Auth displayName from Firestore user document
 * Useful for fixing users whose displayName was lost during social sign-in
 */
const restoreDisplayNameFromFirestore = async (_uid: string): Promise<void> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Cannot restore displayName. No user is currently signed in.");
  }

  logger.debug('Restoring displayName from Firestore for user', { uid: user.uid });

  // We'll handle this at the hook level to avoid circular imports
  // This function signature is kept for future use
  logger.warn('restoreDisplayNameFromFirestore should be called from useAuth hook');
}

// Lazy initialization of social auth providers to avoid module-level Firebase calls during SSR/build
let googleProvider: GoogleAuthProvider | null = null;
let facebookProvider: FacebookAuthProvider | null = null;
let appleProvider: OAuthProvider | null = null;

// SIGN-IN provider getters. These intentionally do NOT force a fresh
// credential prompt — normal sign-in should ride existing SSO when possible
// (fewer clicks). For STEP-UP re-authentication use the `*ReauthProvider`
// getters below, which force a genuine re-auth so Firebase advances
// `auth_time`. Keep the two sets separate: mutating these with a force-prompt
// would regress the everyday login UX.
export const getGoogleProvider = (): GoogleAuthProvider => {
  if (!googleProvider) {
    googleProvider = new GoogleAuthProvider();
    googleProvider.addScope('email');
    googleProvider.addScope('profile');
  }
  return googleProvider;
};

export const getFacebookProvider = (): FacebookAuthProvider => {
  if (!facebookProvider) {
    facebookProvider = new FacebookAuthProvider();
    facebookProvider.addScope('email');
  }
  return facebookProvider;
};

export const getAppleProvider = (): OAuthProvider => {
  if (!appleProvider) {
    appleProvider = new OAuthProvider('apple.com');
    appleProvider.addScope('email');
    appleProvider.addScope('name');
  }
  return appleProvider;
};

// ── STEP-UP REAUTH provider getters ─────────────────────────────────────────
//
// Distinct instances from the sign-in singletons above, configured to FORCE a
// fresh authentication. This is load-bearing for step-up: `reauthenticateWith
// Popup` only advances the ID token's `auth_time` claim when the IdP actually
// re-authenticates the user. Without a force parameter, an already-signed-in
// social user is SSO'd through silently, `auth_time` never moves, and the
// `requireRecentAuth` gate keeps firing — the exact loop that forced a full
// sign-out as the only escape. The provider-specific forcing params:
//   - Google: `prompt: 'login'` — re-prompt for credentials (OIDC standard).
//   - Facebook/Meta: `auth_type: 'reauthenticate'` — Meta's force-reauth flag.
//   - Apple: no documented web force-reauth param; rely on `finalizeReauth`'s
//     `auth_time`-advanced assertion as the safety net (it surfaces a clear
//     "try another method / sign out" message if Apple SSOs silently).
let googleReauthProvider: GoogleAuthProvider | null = null;
let facebookReauthProvider: FacebookAuthProvider | null = null;
let appleReauthProvider: OAuthProvider | null = null;

export const getGoogleReauthProvider = (): GoogleAuthProvider => {
  if (!googleReauthProvider) {
    googleReauthProvider = new GoogleAuthProvider();
    googleReauthProvider.addScope('email');
    googleReauthProvider.addScope('profile');
    googleReauthProvider.setCustomParameters({ prompt: 'login' });
  }
  return googleReauthProvider;
};

export const getFacebookReauthProvider = (): FacebookAuthProvider => {
  if (!facebookReauthProvider) {
    facebookReauthProvider = new FacebookAuthProvider();
    facebookReauthProvider.addScope('email');
    facebookReauthProvider.setCustomParameters({ auth_type: 'reauthenticate' });
  }
  return facebookReauthProvider;
};

export const getAppleReauthProvider = (): OAuthProvider => {
  if (!appleReauthProvider) {
    appleReauthProvider = new OAuthProvider('apple.com');
    appleReauthProvider.addScope('email');
    appleReauthProvider.addScope('name');
  }
  return appleReauthProvider;
};

/**
 * Sign in with Google
 */
const signInWithGoogle = async (): Promise<AuthUser> => {
  try {
    logger.debug('Attempting Google sign-in');
    const auth = getFirebaseAuth();

    // Store existing user data before Google sign-in (for account linking)
    const existingUser = auth.currentUser;
    const existingDisplayName = existingUser?.displayName;

    const result: UserCredential = await signInWithPopup(auth, getGoogleProvider());

    logger.debug('Google sign-in successful', { uid: result.user.uid, googleDisplayName: result.user.displayName, existingDisplayName });

    // Always preserve existing displayName during account linking
    // Social sign-ins should NOT overwrite our app's displayName logic
    if (existingUser && existingUser.uid === result.user.uid && existingDisplayName) {
      logger.debug('Preserving existing displayName after Google linking');
      await updateProfile(result.user, { displayName: existingDisplayName });
      await result.user.reload();
    }
    // For new users, don't set displayName - let signup completion flow handle it
    else if (!existingUser && result.user.displayName) {
      logger.debug('Clearing Google displayName for new user - signup flow will set it');
      await updateProfile(result.user, { displayName: null });
      await result.user.reload();
    }

    return mapFirebaseUserToAuthUser(result.user);
  } catch (error: any) {
    logger.error('Google sign-in failed', { error: error.message || String(error) });

    // Handle popup cancelled gracefully - don't throw error
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      logger.debug('Google sign-in cancelled by user');
      throw new Error('POPUP_CANCELLED', { cause: error });
    }

    throw error;
  }
};

/**
 * Sign in with Facebook/Meta
 */
const signInWithFacebook = async (): Promise<AuthUser> => {
  try {
    logger.debug('Attempting Facebook sign-in');
    const auth = getFirebaseAuth();

    // Store existing user data before Facebook sign-in (for account linking)
    const existingUser = auth.currentUser;
    const existingDisplayName = existingUser?.displayName;

    const result: UserCredential = await signInWithPopup(auth, getFacebookProvider());

    logger.debug('Facebook sign-in successful', { uid: result.user.uid, facebookDisplayName: result.user.displayName, existingDisplayName });

    // Always preserve existing displayName during account linking
    // Social sign-ins should NOT overwrite our app's displayName logic
    if (existingUser && existingUser.uid === result.user.uid && existingDisplayName) {
      logger.debug('Preserving existing displayName after Facebook linking');
      await updateProfile(result.user, { displayName: existingDisplayName });
      await result.user.reload();
    }
    // For new users, don't set displayName - let signup completion flow handle it
    else if (!existingUser && result.user.displayName) {
      logger.debug('Clearing Facebook displayName for new user - signup flow will set it');
      await updateProfile(result.user, { displayName: null });
      await result.user.reload();
    }

    return mapFirebaseUserToAuthUser(result.user);
  } catch (error: any) {
    logger.error('Facebook sign-in failed', { error: error.message || String(error) });

    // Handle popup cancelled gracefully
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      logger.debug('Facebook sign-in cancelled by user');
      throw new Error('POPUP_CANCELLED', { cause: error });
    }

    throw error;
  }
};

/**
 * Sign in with Apple
 */
const signInWithApple = async (): Promise<AuthUser> => {
  try {
    logger.debug('Attempting Apple sign-in');
    const auth = getFirebaseAuth();

    // Store existing user data before Apple sign-in (for account linking)
    const existingUser = auth.currentUser;
    const existingDisplayName = existingUser?.displayName;

    const result: UserCredential = await signInWithPopup(auth, getAppleProvider());

    logger.debug('Apple sign-in successful', { uid: result.user.uid, appleDisplayName: result.user.displayName, existingDisplayName });

    // Always preserve existing displayName during account linking
    // Social sign-ins should NOT overwrite our app's displayName logic
    if (existingUser && existingUser.uid === result.user.uid && existingDisplayName) {
      logger.debug('Preserving existing displayName after Apple linking');
      await updateProfile(result.user, { displayName: existingDisplayName });
      await result.user.reload();
    }
    // For new users, don't set displayName - let signup completion flow handle it
    else if (!existingUser && result.user.displayName) {
      logger.debug('Clearing Apple displayName for new user - signup flow will set it');
      await updateProfile(result.user, { displayName: null });
      await result.user.reload();
    }

    return mapFirebaseUserToAuthUser(result.user);
  } catch (error: any) {
    logger.error('Apple sign-in failed', { error: error.message || String(error) });

    // Handle popup cancelled gracefully
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      logger.debug('Apple sign-in cancelled by user');
      throw new Error('POPUP_CANCELLED', { cause: error });
    }

    throw error;
  }
};

/**
 * Check current email verification status
 * Forces a reload of the current user to get the latest verification state
 */
const checkEmailVerificationStatus = async (): Promise<boolean> => {
  try {
    const auth = getFirebaseAuth();
    if (!auth.currentUser) {
      return false;
    }

    logger.debug('Checking email verification status');
    await auth.currentUser.reload();
    const isVerified = auth.currentUser.emailVerified;
    logger.debug('Email verification status checked', { isVerified });

    return isVerified;
  } catch (error) {
    logger.error('Failed to check email verification', { error: error instanceof Error ? error.message : String(error) });
    return false;
  }
};

/**
 * Forces a refresh of the current auth state
 * Useful after popup operations that might not trigger auth state changes immediately
 */
const refreshAuthState = async (): Promise<void> => {
  const auth = getFirebaseAuth();
  if (auth.currentUser) {
    try {
      // Force token refresh to ensure auth state is current
      await auth.currentUser.getIdToken(true);
    } catch (error) {
      logger.debug('Auth state refresh skipped', { error: error instanceof Error ? error.message : String(error) });
    }
  }
};

/**
 * Signs in a user with a custom Firebase token for client SDK sync.
 * Used in hybrid authentication to sync server session with client SDK.
 * @param customToken - Custom Firebase token generated by Admin SDK
 * @returns Promise that resolves with user credentials
 */
const signInWithToken = async (customToken: string): Promise<UserCredential> => {
  logger.debug('Signing in with custom token for client sync');
  const auth = getFirebaseAuth();

  try {
    const userCredential = await signInWithCustomToken(auth, customToken);
    logger.debug('Client SDK sync successful', { uid: userCredential.user.uid });
    return userCredential;
  } catch (error) {
    logger.error('Client SDK sync failed', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Gets the current user's ID token.
 * @param forceRefresh - If true, forces Firebase to refresh the token even if it's not expired
 * @returns A promise that resolves with the ID token string, or null if no user is signed in.
 */
export const getCurrentUserToken = async (forceRefresh: boolean = false): Promise<IdTokenResult | null> => {
  const auth = getFirebaseAuth();
  if (!auth.currentUser) {
    return null;
  }
  return auth.currentUser.getIdTokenResult(forceRefresh);
};

// ── Phone Auth ─────────────────────────────────────────────────────────────

/**
 * Send SMS verification code to a phone number.
 * Requires a RecaptchaVerifier (created in the hook layer since it needs DOM).
 */
const sendPhoneVerificationCode = async (
  phoneNumber: string,
  appVerifier: ApplicationVerifier
): Promise<ConfirmationResult> => {
  logger.debug('Sending phone verification code', { phoneNumber });
  const auth = getFirebaseAuth();
  const confirmationResult = await firebaseSignInWithPhoneNumber(auth, phoneNumber, appVerifier);
  logger.debug('Phone verification code sent');
  return confirmationResult;
};

/**
 * Link phone credential to the current anonymous user.
 * Preserves the anonymous UID so all cart/order data carries over.
 */
const linkPhoneToAnonymousUser = async (
  verificationId: string,
  verificationCode: string
): Promise<IAuthUser> => {
  logger.debug('Linking phone credential to anonymous user');
  const auth = getFirebaseAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No user currently signed in');
  }
  if (!user.isAnonymous) {
    throw new Error('Current user is not anonymous');
  }

  const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
  const result = await linkWithCredential(user, credential);
  logger.debug('Phone linked to anonymous user', { uid: result.user.uid });
  return mapFirebaseUserToAuthUser(result.user);
};

/**
 * Confirm a phone verification code (for returning customers signing in).
 */
const confirmPhoneVerificationCode = async (
  confirmationResult: ConfirmationResult,
  verificationCode: string
): Promise<IAuthUser> => {
  logger.debug('Confirming phone verification code');
  const result = await confirmationResult.confirm(verificationCode);
  logger.debug('Phone verification confirmed', { uid: result.user.uid });
  return mapFirebaseUserToAuthUser(result.user);
};

/**
 * Recovery path for `auth/credential-already-in-use`: the phone number being
 * linked to the anonymous user already belongs to an existing customer
 * account. We cannot link (the identity is taken) so we sign INTO that existing
 * account instead. The original SMS code is consumed by the failed
 * `linkWithCredential`, so we must reuse the credential Firebase embeds in the
 * error (`PhoneAuthProvider.credentialFromError`) rather than the
 * `confirmationResult`.
 *
 * Returns null if the error carries no recoverable phone credential (caller
 * should re-throw the original error in that case).
 */
const signInWithPhoneCredentialFromError = async (
  error: unknown,
): Promise<IAuthUser | null> => {
  const credential = PhoneAuthProvider.credentialFromError(error as FirebaseError);
  if (!credential) return null;
  const auth = getFirebaseAuth();
  const result = await signInWithCredential(auth, credential);
  logger.debug('Signed into existing phone account after link conflict', {
    uid: result.user.uid,
  });
  return mapFirebaseUserToAuthUser(result.user);
};

/**
 * Switch the active tenant context. Re-mints custom claims so that
 * restaurant-scoped grants for the specified tenant are populated, then
 * refreshes the ID token AND re-mints the session cookie from it.
 *
 * The cookie re-mint is essential: `POST /switch-tenant` only updates the
 * PERSISTENT custom claims (`setCustomUserClaims`). The session cookie minted
 * at sign-in snapshots the OLD `currentTenantDocId` / `restaurants`, and the // scoped-path-ok: claims key, renamed in Phase 4
 * API auth plugin prefers the cookie over the bearer token — so without this
 * step subsequent requests would authenticate off stale claims and deny access
 * to the newly-selected tenant's restaurant-scoped resources. We re-call the
 * existing `/signin` cookie-mint path with the refreshed ID token (idToken →
 * cookie is the standard Firebase exchange `/signin` already implements).
 *
 * @param rememberMe preserve the caller's original session duration; defaults
 *   to `false` (the `/signin` default — a 24h session cookie).
 */
const switchTenantContext = async (
  tenantDocId: string,
  rememberMe = false,
): Promise<void> => {
  logger.debug('Switching tenant context', { tenantDocId });
  await authApiClient.post('/switch-tenant', { tenantDocId });
  // Force a token refresh so the client-side auth state picks up the
  // new claims immediately rather than waiting for natural expiry.
  const currentUser = getFirebaseAuth().currentUser;
  if (currentUser) {
    const freshIdToken = await currentUser.getIdToken(true);
    // Re-mint the session cookie from the refreshed ID token so the cookie
    // (preferred by the API auth plugin) reflects the new tenant context.
    await authApiClient.post('/signin', { idToken: freshIdToken, rememberMe });
  }
  logger.info('Tenant context switched', { tenantDocId });
};

/**
 * Client-side mirror of the step-up window on `POST /auth/sync-verified-emails`.
 *
 * The route declares `fastify.requireRecentAuth(60)` and that plugin's
 * parameter is `maxAgeMinutes` (`apps/api/components/plugins/requireRecentAuth.ts`),
 * so the REAL window is 60 minutes — even though the route's own doc comment
 * says "Step-up gated (60 s)" and "The 60-second window". This constant tracks
 * the CODE, not the comment, because matching the comment would make the client
 * skip syncs the server would happily accept.
 *
 * The seconds-vs-minutes discrepancy is a genuine server-side question (the gate
 * is 60x weaker than its documentation claims) and is deliberately NOT resolved
 * here — tightening a security window is not a performance change. If the server
 * window is corrected to 60 seconds, change this to `60 * 1000`.
 */
const VERIFIED_EMAIL_SYNC_MAX_AUTH_AGE_MS = 60 * 60 * 1000;

/**
 * Sync the caller's verified emails (Firebase providerData) into their Firestore
 * `verifiedEmails[]` set. The endpoint reads providerData SERVER-SIDE — there is
 * no request body. Step-up gated, so this only succeeds while the login is fresh
 * (it is called right after a meaningful auth change). Idempotent + best-effort;
 * a 409 (an email already claimed by another account) is surfaced by the caller.
 */
const syncVerifiedEmails = async (): Promise<void> => {
  // Freshness pre-check — the endpoint is step-up gated and 401s
  // (STALE_AUTH_REAUTH_REQUIRED) for anything older than the window. The doc
  // comment above has always CLAIMED this is only called while the login is
  // fresh, but the caller fires on any meaningful auth change, and a cold page
  // load is one: `auth_time` is the last REAL sign-in and is never advanced by
  // a token refresh, so on every boot of an existing session this was a
  // guaranteed 401 pair in the console. Checking locally turns a doomed
  // round-trip into no round-trip; a genuine post-sign-in call is unaffected
  // because `auth_time` is seconds old there.
  const currentUser = getFirebaseAuth().currentUser;
  if (!currentUser) return;

  try {
    const { authTime } = await currentUser.getIdTokenResult();
    const authTimeMs = authTime ? Date.parse(authTime) : NaN;
    if (Number.isFinite(authTimeMs) &&
        Date.now() - authTimeMs > VERIFIED_EMAIL_SYNC_MAX_AUTH_AGE_MS) {
      logger.debug('Skipping verified-email sync — login is not fresh enough for the step-up gate');
      return;
    }
  } catch {
    // Token read failed. Fall through and let the server decide rather than
    // silently skipping a sync that might be both fresh and needed.
  }

  // Send an explicit empty JSON body. A bodyless POST left the request without a
  // parseable application/json payload, which the API rejected with 415
  // Unsupported Media Type on every auth-state change. The route takes no body;
  // `{}` just makes the content type unambiguous.
  //
  // It never prompts. This runs on page load, not because the person did
  // anything, and the client freshness check above reads the FIREBASE token's
  // `auth_time` while the API reads the session cookie's — which can be hours
  // older (a back-office app on another site signs Firebase in by custom token
  // at boot). A stale refusal here is a skipped sync, not a reason to ask.
  const config: AxiosRequestConfig & NoRecentAuthPromptRequest = { _noRecentAuthPrompt: true };
  await authApiClient.post('/sync-verified-emails', {}, config);
};

export const authService = {
  signIn,
  resolveMfaSignIn,
  signInAnonymously,
  signUp,
  signOut,
  crossAppSignOut,
  sendPasswordResetEmail,
  sendVerificationEmail,
  deleteCurrentUser,
  updateUserProfile,
  reauthenticateWithPassword,
  updatePassword,
  verifyBeforeUpdateEmail,
  isAccountChangeRoutedThroughApi,
  changePasswordThroughApi,
  requestEmailChangeThroughApi,
  confirmPasswordResetThroughApi,
  refreshAuthState,
  signInWithGoogle,
  signInWithFacebook,
  signInWithApple,
  checkEmailVerificationStatus,
  signInWithToken,
  restoreDisplayNameFromFirestore,
  sendPhoneVerificationCode,
  linkPhoneToAnonymousUser,
  confirmPhoneVerificationCode,
  signInWithPhoneCredentialFromError,
  switchTenantContext,
  syncVerifiedEmails,
};
